import {FC, useCallback, useContext} from 'react'
import File from "Entity/File";
import Product from "Entity/Product";
import Choice from "Entity/Property/Choice";
import CardData from "Entity/Collection/ListingSetting/View/CardData";
import {CollectionContext} from "Vendor/Context/CollectionContextProvider";
import useTranslationDataUtils from "Vendor/Utils/TranslationDataUtils";
import PreviewCanvas from "./_PreviewCanvas";
import PreviewImage from "./_PreviewImage";
import {buildZipTree, ExternalFileInput, FolderGrouping, PreviewEntry, ZipPayload} from "./BimboShareFolders";

type BimboShareZipPreviewModalProps = {
    show: boolean
    handleClose: () => void
    title: string
    buckets: { [slug: string]: File[] }
    catalogs: { [slug: string]: Product[] }
    externalFiles: ExternalFileInput[]
    grouping: FolderGrouping
    productWithPDF: boolean
    productUniqueDirectory: boolean
    cardDatasBySlug: { [slug: string]: CardData[] }
}

const slugify = (value: string): string => value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const BimboShareZipPreviewModal: FC<BimboShareZipPreviewModalProps> = ({
    show,
    handleClose,
    title,
    buckets,
    catalogs,
    externalFiles,
    grouping,
    productWithPDF,
    productUniqueDirectory,
    cardDatasBySlug,
}) => {
    const {translate} = useTranslationDataUtils()
    const {getValue} = useContext(CollectionContext)

    const choiceLabel = useCallback((choice: Choice) => {
        const label = translate(choice, 'label')

        return label !== '--unknown--' ? label : choice.value ?? 'no value and no label'
    }, [translate])

    const tree = buildZipTree({
        buckets,
        catalogs,
        externalFiles,
        grouping,
        productWithPDF,
        productUniqueDirectory,
        choiceLabel,
        productName: product => translate(product, 'name'),
        productSlug: product => slugify(translate(product, 'name')),
    })

    const renderImage = (entry: PreviewEntry<ZipPayload>) => {
        const payload = entry.payload

        if (payload.kind === 'file') {
            const fallback = payload.file.mimeType?.includes('image') ? undefined : 'fa-regular fa-file-lines'

            return <PreviewImage src={payload.file.thumbnail?.replaceAll('__SIZING__', 'extra-large')}
                                 alt={entry.name} fallbackIcon={fallback}/>
        }

        if (payload.kind === 'external' && payload.file.isImage && payload.file.previewUrl) {
            return <PreviewImage src={payload.file.previewUrl} alt={entry.name}/>
        }

        const icon = payload.kind === 'productPdf' ? 'fa-regular fa-file-pdf' : 'fa-regular fa-file'

        return <PreviewImage alt={entry.name} fallbackIcon={icon}/>
    }

    const renderDetails = (entry: PreviewEntry<ZipPayload>) => {
        const payload = entry.payload
        let properties: { slug: string, label: string, value: React.ReactNode }[] = []

        if (payload.kind === 'file') {
            properties = (cardDatasBySlug[payload.file.bucketPrimarySlug] ?? [])
                .filter(cardData => cardData.property)
                .map(cardData => ({
                    slug: cardData.property!.slug,
                    label: translate(cardData.property!, 'label'),
                    value: getValue(payload.file, cardData.property!),
                }))
                .filter(property => property.value !== undefined && property.value !== '')
        }

        return <>
            <h6 className="name">{entry.name}</h6>
            {properties.length > 0 &&
                <dl>
                    {properties.map(property =>
                        <div key={property.slug}>
                            <dt>{property.label}</dt>
                            <dd>{property.value}</dd>
                        </div>
                    )}
                </dl>
            }
        </>
    }

    return <PreviewCanvas
        show={show}
        handleClose={handleClose}
        title={title}
        tree={tree}
        renderImage={renderImage}
        renderDetails={renderDetails}
    />
}

export default BimboShareZipPreviewModal
